/**
 * SiteCategoryEditors — per-category form editors for the Site edit dialog.
 *
 * Each editor manages its own state and has individual save/cancel controls.
 * Saves via addModifySite merge-update on the details blob.
 *
 * Categories that store data as separate ObjectItems (procedures, panelConfig,
 * accessGroups, userRoles/permissions) are not editable here — they need
 * dedicated backend endpoints.
 */
import { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, TextField, Button, Divider,
  Checkbox, MenuItem, IconButton,
  Chip, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { api } from '../../api/rpc';
import { pushError, pushSuccess } from '../../stores/notificationStore';
import type { SiteRecord } from '../../api/types';

/* ── Shared sub-components ── */
const FR = ({ l, children }: { l: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75, gap: 1 }}>
    <Typography variant="body2" sx={{ fontSize: 12, minWidth: 150, textAlign: 'right', flexShrink: 0, pt: 0.5 }}>{l}:</Typography>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

const CR = ({ l, c, o }: { l: string; c: boolean; o: (v: boolean) => void }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, ml: '158px' }}>
    <Checkbox size="small" checked={c} onChange={(e) => o(e.target.checked)} sx={{ p: 0.25, mr: 0.5 }} />
    <Typography variant="body2" sx={{ fontSize: 12 }}>{l}</Typography>
  </Box>
);

const fsx = { '& .MuiInputBase-root': { fontSize: 12 } };
const secSx = { fontSize: 12, fontWeight: 600, mb: 1 };
const saveBtnSx = { textTransform: 'none' as const, fontSize: 12, mt: 2 };

const SaveBar = ({ saving, onSave }: { saving: boolean; onSave: () => void }) => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
    <Button size="small" variant="contained" onClick={onSave} disabled={saving} sx={saveBtnSx}>
      {saving ? 'Saving...' : 'Save Changes'}
    </Button>
  </Box>
);

/** Read features sub-object safely */
const feat = (d: Record<string, unknown>) =>
  (typeof d.features === 'object' && d.features ? d.features : {}) as Record<string, unknown>;

interface EditorProps {
  site: SiteRecord;
  d: Record<string, unknown>;
  onSaved: () => void;
}

/* ══════════════════════════════════════════════════════════════════════════
 * Security Editor
 * Keys: d.features.security (password policies, session config)
 * ══════════════════════════════════════════════════════════════════════════ */
export const SecurityEditor = ({ site, d, onSaved }: EditorProps) => {
  const sec = useMemo(() => (feat(d).security ?? {}) as Record<string, unknown>, [d]);
  const [sessionTimeout, setSessionTimeout] = useState(Number(sec.sessionTimeoutMinutes ?? sec.sessionTimeout ?? 30));
  const [minPwdLen, setMinPwdLen] = useState(Number(sec.minPasswordLength ?? sec.minPwdLen ?? 8));
  const [pwdExpiry, setPwdExpiry] = useState(Number(sec.passwordExpiryDays ?? sec.pwdExpDays ?? 0));
  const [maxAttempts, setMaxAttempts] = useState(Number(sec.maxLoginAttempts ?? sec.maxAttempts ?? 5));
  const [lockDuration, setLockDuration] = useState(Number(sec.lockDurationMinutes ?? sec.lockDurationMins ?? 15));
  const [reqUppercase, setReqUppercase] = useState(!!sec.requireUppercase);
  const [reqNumber, setReqNumber] = useState(!!sec.requireNumber);
  const [reqSpecial, setReqSpecial] = useState(!!sec.requireSpecialChar);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(!!sec.twoFactorEnabled);
  const [ipWhitelist, setIpWhitelist] = useState((sec.ipWhitelist ?? '') as string);
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const features = { ...feat(d) };
      features.security = {
        ...sec,
        sessionTimeoutMinutes: sessionTimeout,
        minPasswordLength: minPwdLen,
        passwordExpiryDays: pwdExpiry,
        maxLoginAttempts: maxAttempts,
        lockDurationMinutes: lockDuration,
        requireUppercase: reqUppercase,
        requireNumber: reqNumber,
        requireSpecialChar: reqSpecial,
        twoFactorEnabled,
        ipWhitelist: ipWhitelist || undefined,
      };
      await api.addModifySite({ objKey: site.objKey, details: { features } });
      pushSuccess('Security settings saved');
      onSaved();
    } catch (e) { pushError(`Failed to save: ${e instanceof Error ? e.message : String(e)}`); }
    setSaving(false);
  }, [site, d, sec, sessionTimeout, minPwdLen, pwdExpiry, maxAttempts, lockDuration, reqUppercase, reqNumber, reqSpecial, twoFactorEnabled, ipWhitelist, onSaved]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={secSx}>Session Settings</Typography>
      <FR l="Session Timeout (min)"><TextField size="small" type="number" value={sessionTimeout} onChange={(e) => setSessionTimeout(Number(e.target.value))} sx={{ ...fsx, width: 80 }} /></FR>

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" sx={secSx}>Password Policy</Typography>
      <FR l="Min Password Length"><TextField size="small" type="number" value={minPwdLen} onChange={(e) => setMinPwdLen(Number(e.target.value))} sx={{ ...fsx, width: 80 }} /></FR>
      <FR l="Password Expiry (days)"><TextField size="small" type="number" value={pwdExpiry} onChange={(e) => setPwdExpiry(Number(e.target.value))} sx={{ ...fsx, width: 80 }} placeholder="0 = never" /></FR>
      <CR l="Require uppercase letter" c={reqUppercase} o={setReqUppercase} />
      <CR l="Require number" c={reqNumber} o={setReqNumber} />
      <CR l="Require special character" c={reqSpecial} o={setReqSpecial} />

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" sx={secSx}>Login Lockout</Typography>
      <FR l="Max Login Attempts"><TextField size="small" type="number" value={maxAttempts} onChange={(e) => setMaxAttempts(Number(e.target.value))} sx={{ ...fsx, width: 80 }} /></FR>
      <FR l="Lock Duration (min)"><TextField size="small" type="number" value={lockDuration} onChange={(e) => setLockDuration(Number(e.target.value))} sx={{ ...fsx, width: 80 }} /></FR>

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" sx={secSx}>Two-Factor Authentication</Typography>
      <CR l="Enable 2FA" c={twoFactorEnabled} o={setTwoFactorEnabled} />

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" sx={secSx}>IP Whitelist</Typography>
      <FR l="Allowed IPs"><TextField size="small" fullWidth multiline minRows={2} value={ipWhitelist} onChange={(e) => setIpWhitelist(e.target.value)} sx={fsx} placeholder="Comma-separated IPs or CIDR ranges" /></FR>

      <SaveBar saving={saving} onSave={save} />
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * Reading Workflow Editor
 * Keys: d.features.readingWorkflow
 * ══════════════════════════════════════════════════════════════════════════ */
export const ReadingWorkflowEditor = ({ site, d, onSaved }: EditorProps) => {
  const rw = useMemo(() => (feat(d).readingWorkflow ?? {}) as Record<string, unknown>, [d]);
  const [enabled, setEnabled] = useState(!!rw.readingWorkflowEnabled);
  const [accepting, setAccepting] = useState(!!rw.acceptingStudiesEnabled);
  const [advancedRules, setAdvancedRules] = useState(!!rw.advancedAssignRulesEnabled);
  const [sla, setSla] = useState(!!rw.slaEnabled);
  const [delayedSigning, setDelayedSigning] = useState(!!rw.delayedRepSigningEnabled);
  const [trackLocation, setTrackLocation] = useState(!!rw.trackLocationEnabled);
  const [maxAssigned, setMaxAssigned] = useState(Number(rw.maxRadAssigned ?? 20));
  const [sameRad, setSameRad] = useState(!!rw.assignPatToSameRadEnabled);
  const [sameRadHours, setSameRadHours] = useState(Number(rw.assignPatToSameRadHours ?? 0));
  const [sameRadAlways, setSameRadAlways] = useState(!!rw.assignPatToSameRadAlways);
  const [powerRead, setPowerRead] = useState(!!rw.powerReadEnabled);
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const features = { ...feat(d) };
      features.readingWorkflow = {
        ...rw,
        readingWorkflowEnabled: enabled,
        acceptingStudiesEnabled: accepting,
        advancedAssignRulesEnabled: advancedRules,
        slaEnabled: sla,
        delayedRepSigningEnabled: delayedSigning,
        trackLocationEnabled: trackLocation,
        maxRadAssigned: maxAssigned,
        assignPatToSameRadEnabled: sameRad,
        assignPatToSameRadHours: sameRadHours,
        assignPatToSameRadAlways: sameRadAlways,
        powerReadEnabled: powerRead,
      };
      await api.addModifySite({ objKey: site.objKey, details: { features } });
      pushSuccess('Reading workflow settings saved');
      onSaved();
    } catch (e) { pushError(`Failed to save: ${e instanceof Error ? e.message : String(e)}`); }
    setSaving(false);
  }, [site, d, rw, enabled, accepting, advancedRules, sla, delayedSigning, trackLocation, maxAssigned, sameRad, sameRadHours, sameRadAlways, powerRead, onSaved]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={secSx}>Workflow Toggles</Typography>
      <CR l="Enable Reading Workflow" c={enabled} o={setEnabled} />
      <CR l="Accept studies for reading" c={accepting} o={setAccepting} />
      <CR l="Advanced assignment rules" c={advancedRules} o={setAdvancedRules} />
      <CR l="SLA tracking" c={sla} o={setSla} />
      <CR l="Delayed report signing" c={delayedSigning} o={setDelayedSigning} />
      <CR l="Track reader location" c={trackLocation} o={setTrackLocation} />
      <CR l="Power-read mode" c={powerRead} o={setPowerRead} />

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" sx={secSx}>Assignment</Typography>
      <FR l="Max simultaneous assigned"><TextField size="small" type="number" value={maxAssigned} onChange={(e) => setMaxAssigned(Number(e.target.value))} sx={{ ...fsx, width: 80 }} /></FR>
      <CR l="Assign patient to same radiologist" c={sameRad} o={setSameRad} />
      {sameRad && <>
        <FR l="Look-back window (hours)"><TextField size="small" type="number" value={sameRadHours} onChange={(e) => setSameRadHours(Number(e.target.value))} sx={{ ...fsx, width: 80 }} /></FR>
        <CR l="Always assign to same rad (ignore window)" c={sameRadAlways} o={setSameRadAlways} />
      </>}

      <SaveBar saving={saving} onSave={save} />
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * CDS Workflow Editor
 * Keys: d.cdsManualInputEnabled, d.cdsRadWiseEnabled
 * ══════════════════════════════════════════════════════════════════════════ */
export const CDSWorkflowEditor = ({ site, d, onSaved }: EditorProps) => {
  const [manual, setManual] = useState(!!d.cdsManualInputEnabled);
  const [radwise, setRadwise] = useState(!!d.cdsRadWiseEnabled);
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await api.addModifySite({ objKey: site.objKey, details: { cdsManualInputEnabled: manual, cdsRadWiseEnabled: radwise } });
      pushSuccess('CDS workflow settings saved');
      onSaved();
    } catch (e) { pushError(`Failed to save: ${e instanceof Error ? e.message : String(e)}`); }
    setSaving(false);
  }, [site, manual, radwise, onSaved]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={secSx}>Clinical Decision Support</Typography>
      <CR l="Enable CDS Manual Input" c={manual} o={setManual} />
      <CR l="Enable CDS RadWise Integration" c={radwise} o={setRadwise} />
      <Typography variant="body2" sx={{ fontSize: 11, color: 'text.secondary', mt: 1, ml: '158px' }}>
        CDS vendor configurations are managed in the backend system.
      </Typography>
      <SaveBar saving={saving} onSave={save} />
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * Report Configuration Editor
 * Keys: d.reportHeader
 * ══════════════════════════════════════════════════════════════════════════ */
export const ReportConfigEditor = ({ site, d, onSaved }: EditorProps) => {
  const rh = useMemo(() => (d.reportHeader ?? {}) as Record<string, unknown>, [d]);
  const margins = (rh.margins ?? { top: 72, bottom: 72, left: 72, right: 72 }) as Record<string, number>;
  const [pageSize, setPageSize] = useState((rh.pageSize ?? 'LETTER') as string);
  const [filenamePattern, setFilenamePattern] = useState((rh.filenamePattern ?? '') as string);
  const [headerHtml, setHeaderHtml] = useState((rh.header ?? '') as string);
  const [includeOnEveryPage, setIncludeOnEveryPage] = useState(!!rh.includeReportHeaderOnEveryPage);
  const [includeQR, setIncludeQR] = useState(!!rh.includeQRCode);
  const [marginTop, setMarginTop] = useState(margins.top);
  const [marginBottom, setMarginBottom] = useState(margins.bottom);
  const [marginLeft, setMarginLeft] = useState(margins.left);
  const [marginRight, setMarginRight] = useState(margins.right);
  const [fontsType, setFontsType] = useState((rh.fontsType ?? 'default') as string);
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await api.addModifySite({
        objKey: site.objKey,
        details: {
          reportHeader: {
            ...rh,
            pageSize,
            filenamePattern: filenamePattern || undefined,
            header: headerHtml,
            includeReportHeaderOnEveryPage: includeOnEveryPage,
            includeQRCode: includeQR,
            margins: { top: marginTop, bottom: marginBottom, left: marginLeft, right: marginRight },
            fontsType,
          },
        },
      });
      pushSuccess('Report configuration saved');
      onSaved();
    } catch (e) { pushError(`Failed to save: ${e instanceof Error ? e.message : String(e)}`); }
    setSaving(false);
  }, [site, rh, pageSize, filenamePattern, headerHtml, includeOnEveryPage, includeQR, marginTop, marginBottom, marginLeft, marginRight, fontsType, onSaved]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={secSx}>Page Layout</Typography>
      <FR l="Page Size">
        <TextField size="small" select value={pageSize} onChange={(e) => setPageSize(e.target.value)} sx={{ ...fsx, width: 140 }}>
          <MenuItem value="LETTER" sx={{ fontSize: 12 }}>Letter</MenuItem>
          <MenuItem value="A4" sx={{ fontSize: 12 }}>A4</MenuItem>
          <MenuItem value="LEGAL" sx={{ fontSize: 12 }}>Legal</MenuItem>
        </TextField>
      </FR>
      <FR l="Fonts">
        <TextField size="small" select value={fontsType} onChange={(e) => setFontsType(e.target.value)} sx={{ ...fsx, width: 140 }}>
          <MenuItem value="default" sx={{ fontSize: 12 }}>Default</MenuItem>
          <MenuItem value="serif" sx={{ fontSize: 12 }}>Serif</MenuItem>
          <MenuItem value="sansSerif" sx={{ fontSize: 12 }}>Sans-serif</MenuItem>
          <MenuItem value="mono" sx={{ fontSize: 12 }}>Monospace</MenuItem>
        </TextField>
      </FR>

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" sx={secSx}>Margins (points, 72pt = 1 inch)</Typography>
      <Box sx={{ display: 'flex', gap: 1.5, ml: '158px', mb: 1 }}>
        {([['Top', marginTop, setMarginTop], ['Bottom', marginBottom, setMarginBottom],
           ['Left', marginLeft, setMarginLeft], ['Right', marginRight, setMarginRight]] as const).map(([label, val, set]) => (
          <Box key={label} sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{label}</Typography>
            <TextField size="small" type="number" value={val} onChange={(e) => (set as (v: number) => void)(Number(e.target.value))} sx={{ ...fsx, width: 60, '& .MuiInputBase-root': { fontSize: 11 } }} />
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" sx={secSx}>Header &amp; Options</Typography>
      <FR l="Header HTML"><TextField size="small" fullWidth multiline minRows={3} maxRows={8} value={headerHtml} onChange={(e) => setHeaderHtml(e.target.value)} sx={{ ...fsx, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 10 } }} /></FR>
      <CR l="Include report header on every page" c={includeOnEveryPage} o={setIncludeOnEveryPage} />
      <CR l="Include QR code on report" c={includeQR} o={setIncludeQR} />

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" sx={secSx}>PDF Output</Typography>
      <FR l="Filename Pattern"><TextField size="small" fullWidth value={filenamePattern} onChange={(e) => setFilenamePattern(e.target.value)} sx={fsx} placeholder="{accession}_{patient}.pdf" /></FR>

      <SaveBar saving={saving} onSave={save} />
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * Site Defaults Editor
 * Keys: d.holdList, d.diagnosisList, d.importantFindingReasons,
 *        d.followUpRequiredReasons, d.userSubTypes, d.insuranceRelationships
 * ══════════════════════════════════════════════════════════════════════════ */
export const SiteDefaultsEditor = ({ site, d, onSaved }: EditorProps) => {
  const [diagList, setDiagList] = useState<string[]>(asStringArray(d.diagnosisList));
  const [importantReasons, setImportantReasons] = useState<string[]>(asStringArray(d.importantFindingReasons));
  const [followUpReasons, setFollowUpReasons] = useState<string[]>(asStringArray(d.followUpRequiredReasons));
  const [holdReasons, setHoldReasons] = useState<string[]>(() => {
    const h = d.holdList;
    if (Array.isArray(h)) return h.map(String);
    if (h && typeof h === 'object') return Object.keys(h as Record<string, unknown>);
    return [];
  });
  const [newDiag, setNewDiag] = useState('');
  const [newImportant, setNewImportant] = useState('');
  const [newFollowUp, setNewFollowUp] = useState('');
  const [newHoldReason, setNewHoldReason] = useState('');
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await api.addModifySite({
        objKey: site.objKey,
        details: {
          diagnosisList: diagList,
          importantFindingReasons: importantReasons,
          followUpRequiredReasons: followUpReasons,
          holdList: holdReasons,
        },
      });
      pushSuccess('Site defaults saved');
      onSaved();
    } catch (e) { pushError(`Failed to save: ${e instanceof Error ? e.message : String(e)}`); }
    setSaving(false);
  }, [site, diagList, importantReasons, followUpReasons, holdReasons, onSaved]);

  return (
    <Box>
      <ListEditor label="Diagnosis Options" items={diagList} setItems={setDiagList} newItem={newDiag} setNewItem={setNewDiag} />
      <Divider sx={{ my: 2 }} />
      <ListEditor label="Important Finding Reasons" items={importantReasons} setItems={setImportantReasons} newItem={newImportant} setNewItem={setNewImportant} />
      <Divider sx={{ my: 2 }} />
      <ListEditor label="Follow-Up Required Reasons" items={followUpReasons} setItems={setFollowUpReasons} newItem={newFollowUp} setNewItem={setNewFollowUp} />
      <Divider sx={{ my: 2 }} />
      <ListEditor label="Hold Reasons" items={holdReasons} setItems={setHoldReasons} newItem={newHoldReason} setNewItem={setNewHoldReason} />

      <SaveBar saving={saving} onSave={save} />
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * Insurance / Payment Editor
 * Keys: d.insuranceRelationships
 * ══════════════════════════════════════════════════════════════════════════ */
export const InsurancePaymentEditor = ({ site, d, onSaved }: EditorProps) => {
  const [relationships, setRelationships] = useState<string[]>(asStringArray(d.insuranceRelationships));
  const [newRel, setNewRel] = useState('');
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await api.addModifySite({ objKey: site.objKey, details: { insuranceRelationships: relationships } });
      pushSuccess('Insurance settings saved');
      onSaved();
    } catch (e) { pushError(`Failed to save: ${e instanceof Error ? e.message : String(e)}`); }
    setSaving(false);
  }, [site, relationships, onSaved]);

  return (
    <Box>
      <ListEditor label="Insurance Relationships" items={relationships} setItems={setRelationships} newItem={newRel} setNewItem={setNewRel} />
      <SaveBar saving={saving} onSave={save} />
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * Image Viewing / System Labels Editor
 * Keys: d.view_sys_labels, d.labelDateFormat, etc.
 * ══════════════════════════════════════════════════════════════════════════ */
export const ImageViewingEditor = ({ site, d, onSaved }: EditorProps) => {
  const labels = (d.view_sys_labels ?? {}) as Record<string, unknown>;
  const [labelDateFmt, setLabelDateFmt] = useState(Number(d.labelDateFormat ?? d.siteDateFormat ?? 0));
  const [labelClockFmt, setLabelClockFmt] = useState(Number(d.labelClockFormat ?? d.siteClockFormat ?? 0));
  const [labelSeparator, setLabelSeparator] = useState(Number(d.labelSeparator ?? d.siteSeparator ?? 0));
  const [labelsJson, setLabelsJson] = useState(JSON.stringify(labels, null, 2));
  const [labelsJsonError, setLabelsJsonError] = useState('');
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      let parsedLabels: unknown;
      try { parsedLabels = JSON.parse(labelsJson); } catch { pushError('Per-modality layouts contain invalid JSON'); setSaving(false); return; }
      await api.addModifySite({
        objKey: site.objKey,
        details: {
          labelDateFormat: labelDateFmt,
          labelClockFormat: labelClockFmt,
          labelSeparator,
          view_sys_labels: parsedLabels,
        },
      });
      pushSuccess('Image viewing settings saved');
      onSaved();
    } catch (e) { pushError(`Failed to save: ${e instanceof Error ? e.message : String(e)}`); }
    setSaving(false);
  }, [site, labelDateFmt, labelClockFmt, labelSeparator, labelsJson, onSaved]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={secSx}>Viewer Label Formats</Typography>
      <FR l="Label Date Format">
        <TextField size="small" select value={labelDateFmt} onChange={(e) => setLabelDateFmt(Number(e.target.value))} sx={{ ...fsx, width: 140 }}>
          <MenuItem value={0} sx={{ fontSize: 12 }}>MM/DD/YYYY</MenuItem>
          <MenuItem value={1} sx={{ fontSize: 12 }}>DD/MM/YYYY</MenuItem>
          <MenuItem value={2} sx={{ fontSize: 12 }}>YYYY/MM/DD</MenuItem>
        </TextField>
      </FR>
      <FR l="Label Clock Format">
        <TextField size="small" select value={labelClockFmt} onChange={(e) => setLabelClockFmt(Number(e.target.value))} sx={{ ...fsx, width: 140 }}>
          <MenuItem value={0} sx={{ fontSize: 12 }}>12-hour</MenuItem>
          <MenuItem value={1} sx={{ fontSize: 12 }}>24-hour</MenuItem>
        </TextField>
      </FR>
      <FR l="Label Separator">
        <TextField size="small" select value={labelSeparator} onChange={(e) => setLabelSeparator(Number(e.target.value))} sx={{ ...fsx, width: 80 }}>
          <MenuItem value={0} sx={{ fontSize: 12 }}>/</MenuItem>
          <MenuItem value={1} sx={{ fontSize: 12 }}>-</MenuItem>
          <MenuItem value={2} sx={{ fontSize: 12 }}>.</MenuItem>
        </TextField>
      </FR>

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" sx={secSx}>Per-Modality Label Layouts</Typography>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', ml: '158px', mb: 0.5 }}>JSON object keyed by modality code (CT, MR, CR, etc.).</Typography>
      <FR l="Layouts">
        <TextField
          size="small" fullWidth multiline minRows={4} maxRows={16}
          value={labelsJson}
          onChange={(e) => { setLabelsJson(e.target.value); setLabelsJsonError(''); }}
          error={!!labelsJsonError}
          helperText={labelsJsonError || undefined}
          sx={{ ...fsx, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 10 } }}
        />
      </FR>

      <SaveBar saving={saving} onSave={save} />
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * Feature-Gated Config Editors
 * Each reads from d.features.X or d.X (with correct key)
 * ══════════════════════════════════════════════════════════════════════════ */

/** Generic feature-config editor: booleans + key-value display + save */
export const FeatureConfigEditor = ({ site, d, onSaved, featureKey, subKeys }: EditorProps & {
  featureKey: string;
  subKeys?: { key: string; label: string; type: 'bool' | 'string' | 'number' }[];
}) => {
  // Try d.features[featureKey] first, then d[featureKey] as fallback
  const raw = (feat(d)[featureKey] ?? d[featureKey] ?? {}) as Record<string, unknown>;
  const [values, setValues] = useState<Record<string, unknown>>({ ...raw });
  const [saving, setSaving] = useState(false);

  const setValue = (key: string, val: unknown) => setValues((p) => ({ ...p, [key]: val }));

  const save = useCallback(async () => {
    setSaving(true);
    try {
      // Save back under features[featureKey]
      const features = { ...feat(d) };
      features[featureKey] = values;
      await api.addModifySite({ objKey: site.objKey, details: { features } });
      pushSuccess('Settings saved');
      onSaved();
    } catch (e) { pushError(`Failed to save: ${e instanceof Error ? e.message : String(e)}`); }
    setSaving(false);
  }, [site, d, featureKey, values, onSaved]);

  // Auto-detect fields if subKeys not provided
  const fields = subKeys ?? Object.entries(raw).map(([key, val]) => ({
    key,
    label: camelToLabel(key),
    type: (typeof val === 'boolean' ? 'bool' : typeof val === 'number' ? 'number' : 'string') as 'bool' | 'string' | 'number',
  }));

  if (fields.length === 0) {
    return (
      <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>
        No configuration data found for this category.
      </Typography>
    );
  }

  return (
    <Box>
      {fields.map((f) =>
        f.type === 'bool' ? (
          <CR key={f.key} l={f.label} c={!!values[f.key]} o={(v) => setValue(f.key, v)} />
        ) : (
          <FR key={f.key} l={f.label}>
            <TextField
              size="small"
              type={f.type === 'number' ? 'number' : 'text'}
              fullWidth={f.type === 'string'}
              value={values[f.key] ?? ''}
              onChange={(e) => setValue(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)}
              sx={{ ...fsx, ...(f.type === 'number' ? { width: 80 } : {}) }}
            />
          </FR>
        ),
      )}
      <SaveBar saving={saving} onSave={save} />
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * Unavailable Category placeholder (data stored as separate ObjectItems)
 * ══════════════════════════════════════════════════════════════════════════ */
export const UnavailableCategory = ({ category }: { category: string }) => (
  <Box sx={{ py: 2 }}>
    <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>
      {category} configuration is stored as separate server objects and requires
      dedicated management endpoints. Currently managed via the GWT admin interface.
    </Typography>
    <Chip label="Backend endpoint needed" size="small" sx={{ fontSize: 10, mt: 1 }} />
  </Box>
);

/* ══════════════════════════════════════════════════════════════════════════
 * Shared helpers
 * ══════════════════════════════════════════════════════════════════════════ */

function asStringArray(val: unknown): string[] {
  if (Array.isArray(val)) return val.map(String);
  return [];
}

function camelToLabel(s: string): string {
  return s.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
}

/* ══════════════════════════════════════════════════════════════════════════
 * Login Configuration Editor
 * Keys: d.features.adAuth, d.ssoProviders
 * ══════════════════════════════════════════════════════════════════════════ */
export const LoginConfigEditor = ({ site, d, onSaved }: EditorProps) => {
  const adAuth = useMemo(() => ((feat(d).adAuth ?? {}) as Record<string, unknown>), [d]);
  const [ldapEnabled, setLdapEnabled] = useState(adAuth.enabled === 'on');
  const [ldapUrl, setLdapUrl] = useState((adAuth.ldapUrl ?? '') as string);
  const [ldapDomain, setLdapDomain] = useState((adAuth.ldapDomain ?? '') as string);
  const [ldapBaseDn, setLdapBaseDn] = useState((adAuth.ldapBaseDn ?? '') as string);
  const [tryRwLogin, setTryRwLogin] = useState(adAuth.tryRWLogin === 'on');
  const [ldapAdminUser, setLdapAdminUser] = useState((adAuth.adminUser ?? '') as string);
  const [ldapAdminPassword, setLdapAdminPassword] = useState('');
  const [ssoJson, setSsoJson] = useState(JSON.stringify(d.ssoProviders ?? {}, null, 2));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      let parsedSso: unknown;
      try { parsedSso = JSON.parse(ssoJson); } catch { pushError('SSO Providers contains invalid JSON'); setSaving(false); return; }
      const features = { ...feat(d) };
      features.adAuth = {
        ...adAuth,
        enabled: ldapEnabled ? 'on' : 'off',
        ldapUrl: ldapUrl || undefined,
        ldapDomain: ldapDomain || undefined,
        ldapBaseDn: ldapBaseDn || undefined,
        tryRWLogin: tryRwLogin ? 'on' : 'off',
        adminUser: ldapAdminUser || undefined,
        ...(ldapAdminPassword ? { adminPassword: ldapAdminPassword } : {}),
      };
      await api.addModifySite({ objKey: site.objKey, details: { features, ssoProviders: parsedSso } });
      pushSuccess('Login configuration saved');
      onSaved();
    } catch (e) { pushError(`Failed to save: ${e instanceof Error ? e.message : String(e)}`); }
    setSaving(false);
  }, [site, d, adAuth, ldapEnabled, ldapUrl, ldapDomain, ldapBaseDn, tryRwLogin, ldapAdminUser, ldapAdminPassword, ssoJson, onSaved]);

  const testLdap = useCallback(async () => {
    setTesting(true);
    try {
      await api.testLdapConnection(ldapUrl, ldapDomain);
      pushSuccess('LDAP connection test successful');
    } catch (e) {
      pushError(`LDAP test failed: ${e instanceof Error ? e.message : String(e)}`);
    }
    setTesting(false);
  }, [ldapUrl, ldapDomain]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={secSx}>Active Directory / LDAP</Typography>
      <CR l="Enable LDAP Authentication" c={ldapEnabled} o={setLdapEnabled} />
      <FR l="LDAP URL"><TextField size="small" fullWidth value={ldapUrl} onChange={(e) => setLdapUrl(e.target.value)} disabled={!ldapEnabled} sx={fsx} placeholder="ldap://hostname:389" /></FR>
      <FR l="Domain"><TextField size="small" fullWidth value={ldapDomain} onChange={(e) => setLdapDomain(e.target.value)} disabled={!ldapEnabled} sx={fsx} placeholder="example.com" /></FR>
      <FR l="Base DN"><TextField size="small" fullWidth value={ldapBaseDn} onChange={(e) => setLdapBaseDn(e.target.value)} disabled={!ldapEnabled} sx={fsx} placeholder="DC=example,DC=com" /></FR>
      <CR l="Allow BRIT login if LDAP auth fails" c={tryRwLogin} o={setTryRwLogin} />
      <Divider sx={{ my: 1 }} />
      <FR l="Admin User"><TextField size="small" fullWidth value={ldapAdminUser} onChange={(e) => setLdapAdminUser(e.target.value)} disabled={!ldapEnabled} sx={fsx} placeholder="cn=admin,dc=example,dc=com" /></FR>
      <FR l="Admin Password"><TextField size="small" fullWidth type="password" value={ldapAdminPassword} onChange={(e) => setLdapAdminPassword(e.target.value)} disabled={!ldapEnabled} sx={fsx} placeholder="(leave blank to keep current)" /></FR>
      <Box sx={{ ml: '158px', mt: 0.75, mb: 1 }}>
        <Button size="small" variant="outlined" onClick={testLdap} disabled={!ldapEnabled || !ldapUrl || testing} sx={saveBtnSx}>
          {testing ? 'Testing…' : 'Test LDAP Connection'}
        </Button>
      </Box>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" sx={secSx}>SSO Providers</Typography>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', ml: '158px', mb: 0.5 }}>JSON configuration for SSO providers (SAML, OAuth2, etc.).</Typography>
      <FR l="SSO Config">
        <TextField size="small" fullWidth multiline minRows={4} maxRows={12} value={ssoJson} onChange={(e) => setSsoJson(e.target.value)} sx={{ ...fsx, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 10 } }} />
      </FR>
      <SaveBar saving={saving} onSave={save} />
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * HL7 Configuration Editor
 * Keys: d.keepArchivedHL7Period, d.hl7inbound, d.hl7outbound
 * ══════════════════════════════════════════════════════════════════════════ */
interface HL7Conn {
  name?: string;
  host?: string;
  port?: number;
  enabled?: boolean;
  encoding?: string;
  [key: string]: unknown;
}

const HL7ConnList = ({
  type, conns, onUpdate, onRemove, onAdd,
}: {
  type: 'Inbound' | 'Outbound';
  conns: HL7Conn[];
  onUpdate: (idx: number, field: string, val: unknown) => void;
  onRemove: (idx: number) => void;
  onAdd: () => void;
}) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Typography variant="subtitle2" sx={{ fontSize: 12, fontWeight: 600 }}>{type} Connections ({conns.length})</Typography>
      <Button size="small" startIcon={<AddIcon />} onClick={onAdd} sx={{ textTransform: 'none', fontSize: 11, py: 0 }}>Add</Button>
    </Box>
    {conns.length === 0 && <Typography sx={{ fontSize: 11, color: 'text.secondary', fontStyle: 'italic', ml: 2, mb: 1 }}>No {type.toLowerCase()} connections defined.</Typography>}
    {conns.map((c, idx) => (
      <Box key={idx} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {([['Name', 'name', 140, 'text', undefined], ['Host', 'host', 160, 'text', 'hostname or IP'], ['Port', 'port', 72, 'number', undefined], ['Encoding', 'encoding', 90, 'text', 'UTF-8']] as const).map(([label, field, width, t, ph]) => (
            <Box key={field}>
              <Typography sx={{ fontSize: 10, color: 'text.secondary', mb: 0.25 }}>{label}</Typography>
              <TextField
                size="small" type={t}
                value={field === 'port' ? (c.port ?? 11112) : (c[field] ?? '')}
                onChange={(e) => onUpdate(idx, field, t === 'number' ? Number(e.target.value) : e.target.value)}
                placeholder={ph}
                sx={{ ...fsx, width }}
              />
            </Box>
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
            <Checkbox size="small" checked={c.enabled !== false} onChange={(e) => onUpdate(idx, 'enabled', e.target.checked)} sx={{ p: 0.25 }} />
            <Typography sx={{ fontSize: 11 }}>Enabled</Typography>
          </Box>
          <IconButton size="small" onClick={() => onRemove(idx)} sx={{ mb: 0.25, color: 'error.main' }}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
        </Box>
      </Box>
    ))}
  </Box>
);

export const HL7ConfigEditor = ({ site, d, onSaved }: EditorProps) => {
  const [archivePeriod, setArchivePeriod] = useState(Number(d.keepArchivedHL7Period ?? 20));
  const [inbound, setInbound] = useState<HL7Conn[]>(Array.isArray(d.hl7inbound) ? d.hl7inbound as HL7Conn[] : []);
  const [outbound, setOutbound] = useState<HL7Conn[]>(Array.isArray(d.hl7outbound) ? d.hl7outbound as HL7Conn[] : []);
  const [saving, setSaving] = useState(false);

  const updateConn = useCallback((type: 'inbound' | 'outbound', idx: number, field: string, val: unknown) => {
    const setter = type === 'inbound' ? setInbound : setOutbound;
    setter((arr) => arr.map((c, i) => i === idx ? { ...c, [field]: val } : c));
  }, []);

  const removeConn = useCallback((type: 'inbound' | 'outbound', idx: number) => {
    const setter = type === 'inbound' ? setInbound : setOutbound;
    setter((arr) => arr.filter((_, i) => i !== idx));
  }, []);

  const addConn = useCallback((type: 'inbound' | 'outbound') => {
    const setter = type === 'inbound' ? setInbound : setOutbound;
    setter((arr) => [...arr, { name: '', host: '', port: 11112, enabled: true, encoding: 'UTF-8' }]);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await api.addModifySite({
        objKey: site.objKey,
        details: { keepArchivedHL7Period: archivePeriod, hl7inbound: inbound, hl7outbound: outbound },
      });
      pushSuccess('HL7 configuration saved');
      onSaved();
    } catch (e) { pushError(`Failed to save: ${e instanceof Error ? e.message : String(e)}`); }
    setSaving(false);
  }, [site, archivePeriod, inbound, outbound, onSaved]);

  return (
    <Box>
      <FR l="Archive Period (days)">
        <TextField size="small" type="number" value={archivePeriod} onChange={(e) => setArchivePeriod(Number(e.target.value))} sx={{ ...fsx, width: 80 }} />
      </FR>
      <Divider sx={{ my: 1.5 }} />
      <HL7ConnList
        type="Inbound" conns={inbound}
        onUpdate={(i, f, v) => updateConn('inbound', i, f, v)}
        onRemove={(i) => removeConn('inbound', i)}
        onAdd={() => addConn('inbound')}
      />
      <Divider sx={{ my: 1.5 }} />
      <HL7ConnList
        type="Outbound" conns={outbound}
        onUpdate={(i, f, v) => updateConn('outbound', i, f, v)}
        onRemove={(i) => removeConn('outbound', i)}
        onAdd={() => addConn('outbound')}
      />
      <SaveBar saving={saving} onSave={save} />
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * Billing Invoice Editor
 * Keys: d.billingReport
 * ══════════════════════════════════════════════════════════════════════════ */
export const BillingEditor = ({ site, d, onSaved }: EditorProps) => {
  const b = useMemo(() => (d.billingReport ?? {}) as Record<string, unknown>, [d.billingReport]);
  const [enabled, setEnabled] = useState(!!b.enabled);
  const [baseFee, setBaseFee] = useState(String(b.baseFee ?? '0'));
  const [minCharge, setMinCharge] = useState(String(b.minCharge ?? '0'));
  const [invoicePrefix, setInvoicePrefix] = useState((b.invoicePrefix ?? '') as string);
  const [taxRate, setTaxRate] = useState(String(b.taxRate ?? '0'));
  const [paymentTerms, setPaymentTerms] = useState(String(b.paymentTerms ?? '30'));
  const [billingEmail, setBillingEmail] = useState((b.billingEmail ?? '') as string);
  const [currency, setCurrency] = useState((b.currency ?? 'USD') as string);
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await api.addModifySite({
        objKey: site.objKey,
        details: {
          billingReport: {
            ...b,
            enabled,
            baseFee: parseFloat(baseFee) || 0,
            minCharge: parseFloat(minCharge) || 0,
            invoicePrefix: invoicePrefix || undefined,
            taxRate: parseFloat(taxRate) || 0,
            paymentTerms: parseInt(paymentTerms, 10) || 30,
            billingEmail: billingEmail || undefined,
            currency,
          },
        },
      });
      pushSuccess('Billing settings saved');
      onSaved();
    } catch (e) { pushError(`Failed to save: ${e instanceof Error ? e.message : String(e)}`); }
    setSaving(false);
  }, [site, b, enabled, baseFee, minCharge, invoicePrefix, taxRate, paymentTerms, billingEmail, currency, onSaved]);

  return (
    <Box>
      <CR l="Billing Enabled" c={enabled} o={setEnabled} />
      <Divider sx={{ my: 1 }} />
      <FR l="Invoice Prefix"><TextField size="small" fullWidth value={invoicePrefix} onChange={(e) => setInvoicePrefix(e.target.value)} disabled={!enabled} sx={fsx} placeholder="e.g. INV-" /></FR>
      <FR l="Base Fee ($)"><TextField size="small" type="number" value={baseFee} onChange={(e) => setBaseFee(e.target.value)} disabled={!enabled} sx={{ ...fsx, width: 100 }} /></FR>
      <FR l="Min Charge ($)"><TextField size="small" type="number" value={minCharge} onChange={(e) => setMinCharge(e.target.value)} disabled={!enabled} sx={{ ...fsx, width: 100 }} /></FR>
      <FR l="Tax Rate (%)"><TextField size="small" type="number" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} disabled={!enabled} sx={{ ...fsx, width: 80 }} /></FR>
      <FR l="Payment Terms (days)"><TextField size="small" type="number" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} disabled={!enabled} sx={{ ...fsx, width: 80 }} /></FR>
      <Divider sx={{ my: 1 }} />
      <FR l="Billing Email"><TextField size="small" fullWidth type="email" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} disabled={!enabled} sx={fsx} /></FR>
      <FR l="Currency">
        <TextField size="small" select value={currency} onChange={(e) => setCurrency(e.target.value)} disabled={!enabled} sx={{ ...fsx, width: 100 }}>
          {['USD', 'CAD', 'EUR', 'GBP', 'AUD'].map((c) => <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>)}
        </TextField>
      </FR>
      <SaveBar saving={saving} onSave={save} />
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
 * Panel Configuration Editor
 * Key: d.panelConfig
 * ══════════════════════════════════════════════════════════════════════════ */
export const PanelConfigEditor = ({ site, d, onSaved }: EditorProps) => {
  const [configJson, setConfigJson] = useState(JSON.stringify(d.panelConfig ?? {}, null, 2));
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      let parsed: unknown;
      try { parsed = JSON.parse(configJson); } catch { pushError('Invalid JSON in panel configuration'); setSaving(false); return; }
      await api.addModifySite({ objKey: site.objKey, details: { panelConfig: parsed } });
      pushSuccess('Panel configuration saved');
      onSaved();
    } catch (e) { pushError(`Failed to save: ${e instanceof Error ? e.message : String(e)}`); }
    setSaving(false);
  }, [site, configJson, onSaved]);

  return (
    <Box>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1 }}>
        Panel configuration controls which worklist columns and tabs are visible by default for users at this site.
        Edit the JSON blob to add or remove column/tab definitions.
      </Typography>
      <TextField
        size="small" fullWidth multiline minRows={8} maxRows={24}
        value={configJson}
        onChange={(e) => setConfigJson(e.target.value)}
        sx={{ ...fsx, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 10 } }}
      />
      <SaveBar saving={saving} onSave={save} />
    </Box>
  );
};

/** Reusable list editor (add/remove string items) */
const ListEditor = ({ label, items, setItems, newItem, setNewItem }: {
  label: string;
  items: string[];
  setItems: (v: string[]) => void;
  newItem: string;
  setNewItem: (v: string) => void;
}) => (
  <Box>
    <Typography variant="subtitle2" sx={secSx}>{label} ({items.length})</Typography>
    <Table size="small" sx={{ maxWidth: 500, ml: '158px' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontSize: 11, fontWeight: 600, py: 0.25 }}>Value</TableCell>
          <TableCell sx={{ fontSize: 11, fontWeight: 600, py: 0.25, width: 40 }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item, idx) => (
          <TableRow key={idx}>
            <TableCell sx={{ fontSize: 11, py: 0.25 }}>{item}</TableCell>
            <TableCell sx={{ py: 0.25 }}>
              <IconButton size="small" onClick={() => setItems(items.filter((_, i) => i !== idx))} sx={{ width: 20, height: 20 }}>
                <DeleteIcon sx={{ fontSize: 12, color: 'error.main' }} />
              </IconButton>
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell sx={{ py: 0.25 }}>
            <TextField
              size="small" fullWidth value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newItem.trim()) {
                  setItems([...items, newItem.trim()]);
                  setNewItem('');
                }
              }}
              placeholder="Add new..."
              sx={{ '& .MuiInputBase-root': { fontSize: 11, height: 24 } }}
            />
          </TableCell>
          <TableCell sx={{ py: 0.25 }}>
            <IconButton
              size="small"
              disabled={!newItem.trim()}
              onClick={() => { setItems([...items, newItem.trim()]); setNewItem(''); }}
              sx={{ width: 20, height: 20 }}
            >
              <AddIcon sx={{ fontSize: 12. }} />
            </IconButton>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </Box>
);
